import { useEffect, useRef } from "react";

const ACTIVE_CHAT_REFRESH_INTERVAL = 60000;
const CHAT_LIST_REFRESH_INTERVAL = 120000;
const HIDDEN_TAB_REFRESH_INTERVAL = 300000;

const isPageHidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

const getRefreshInterval = (hasActiveConversation) => {
  if (isPageHidden()) return HIDDEN_TAB_REFRESH_INTERVAL;
  return hasActiveConversation
    ? ACTIVE_CHAT_REFRESH_INTERVAL
    : CHAT_LIST_REFRESH_INTERVAL;
};

export default function useAdaptiveChatPolling({
  enabled,
  hasActiveConversation,
  onPoll,
}) {
  const onPollRef = useRef(onPoll);
  const pollingRef = useRef(false);

  useEffect(() => {
    onPollRef.current = onPoll;
  }, [onPoll]);

  useEffect(() => {
    if (!enabled) return undefined;

    let timeoutId;
    let cancelled = false;

    const poll = async () => {
      if (pollingRef.current) return;
      pollingRef.current = true;
      try {
        await onPollRef.current();
      } finally {
        pollingRef.current = false;
      }
    };

    const scheduleNextPoll = () => {
      window.clearTimeout(timeoutId);
      if (cancelled) return;
      timeoutId = window.setTimeout(async () => {
        await poll();
        scheduleNextPoll();
      }, getRefreshInterval(hasActiveConversation));
    };

    const handleVisibilityChange = () => {
      if (!isPageHidden()) poll();
      scheduleNextPoll();
    };

    scheduleNextPoll();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, hasActiveConversation]);
}
